<script setup lang="ts">
import { reactive, ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { staticPageHref, accountHref } from '@/router/products'
import { useAuth } from '@/composables/useAuth'
import { updateProfile } from '@/services/account'
import { ApiError } from '@/services/http'
import {
  countryLabel,
  DEFAULT_COUNTRY,
  COUNTRY_CODES_PHONE,
  type PhoneCountryCode,
  type AddressCountryCode,
} from '@/data/countries'
import { toE164 } from '@/utils/phone'
import { formatSsn, normalizeSsn, validateSsn, SSN_ERROR_KEYS } from '@/utils/ssn'
import { parsePhoneNumber } from 'libphonenumber-js/min'
import PhoneField from '@/components/account/PhoneField.vue'
import AddressFields from '@/components/account/AddressFields.vue'
import AccountLayout from '@/components/layout/AccountLayout.vue'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { member, reloadMember, memberLoaded } = useAuth()

const currentLocale = computed<Locale>(() => (route.meta.locale as Locale) ?? DEFAULT_LOCALE)
const loginHref = computed(() => staticPageHref('login', currentLocale.value))
const contactHref = computed(() => accountHref('contact', currentLocale.value))
useHead({ title: computed(() => `${t('auth.account.info.navTitle')} — MySmerra`) })

/** Accès réservé + ouverture directe du formulaire depuis le dashboard
 *  (lien « Modifier mes informations » → ?edit=info). */
onMounted(() => {
  if (!member.value) {
    router.replace(loginHref.value)
    return
  }
  if (route.query.edit !== 'info') return
  // Attendre que `GET /me` ait répondu pour pré-remplir avec le profil réel
  // (au chargement direct de l'URL, member est d'abord un placeholder vide).
  if (memberLoaded.value) {
    startEdit()
  } else {
    const stop = watch(memberLoaded, (loaded) => {
      if (loaded) {
        startEdit()
        stop()
      }
    })
  }
})

const birthDateLabel = computed(() => {
  const iso = member.value?.birthDate
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(locale.value, { day: '2-digit', month: '2-digit', year: 'numeric' })
})

/** Lignes d'informations : clé i18n + icône + valeur. L'adresse est traitée à part. */
type IconName = 'user' | 'cake' | 'shield' | 'hash' | 'phone' | 'pin'

/**
 * `editable` drives the pencil shown beside a value. It marks what the form below actually
 * accepts, which is not the same as what is displayed: name, birth date and member number are
 * read-only for everyone, and the social security number is editable exactly once, while it is
 * still missing. Derived from the same condition the form uses, so the pencil cannot promise an
 * edit the form then refuses.
 */
const rows = computed<{ key: string; icon: IconName; value: string; editable: boolean }[]>(() => {
  const m = member.value
  if (!m) return []
  return [
    { key: 'lastName', icon: 'user', value: m.lastName, editable: false },
    { key: 'firstName', icon: 'user', value: m.firstName, editable: false },
    { key: 'birthDate', icon: 'cake', value: birthDateLabel.value, editable: false },
    // An empty cell reads as a display bug. Saying the number is missing is what makes the
    // form below discoverable at all, since that form only exists for members who have none.
    {
      key: 'nir',
      icon: 'shield',
      value: m.nir || t('auth.account.info.nirMissing'),
      editable: canEnterSsn.value,
    },
    { key: 'memberNumber', icon: 'hash', value: m.memberNumber, editable: false },
    { key: 'phone', icon: 'phone', value: m.phone, editable: true },
  ]
})

/* --- Édition : téléphone et adresse (PATCH /users). Nom/prénom non modifiables. --- */
const editing = ref(false)
const saving = ref(false)
const done = ref(false)
const saveError = ref<string | null>(null)
const addressFields = ref<InstanceType<typeof AddressFields> | null>(null)

const form = reactive<{ phone: string; phoneCountry: PhoneCountryCode; ssn: string }>({
  phone: '',
  phoneCountry: DEFAULT_COUNTRY,
  ssn: '',
})
const ssnError = ref<string | null>(null)

/**
 * Whether the form offers the NIR at all. A member who already has one must not be able to
 * change it: the number identifies them to the health insurance and to ASSIA, a correction is a
 * supported case rather than a self service edit, and their contracts have already been filed
 * under it. So the field is not disabled, it is simply absent, which is the only way a value
 * cannot be submitted by accident or on purpose.
 *
 * Read from the member rather than from a local flag so that it settles on its own once the
 * number is saved and `/me` is read again.
 */
const canEnterSsn = computed(() => !member.value?.nir)
const addr = reactive<{ line1: string; line2: string; city: string; postcode: string; country: AddressCountryCode }>({
  line1: '',
  line2: '',
  city: '',
  postcode: '',
  country: DEFAULT_COUNTRY,
})

/** Libellé pays localisé (affichage en lecture seule). */
const countryName = (code: string) => countryLabel(code, currentLocale.value)

/** Décompose un numéro E.164 (`/me`) en numéro national + pays, pour pré-remplir
 *  le champ téléphone (sinon FR par défaut, champ vide). */
function parseMemberPhone(value: string): { national: string; country: PhoneCountryCode } {
  if (value) {
    try {
      const p = parsePhoneNumber(value)
      if (p?.country && (COUNTRY_CODES_PHONE as readonly string[]).includes(p.country)) {
        return { national: p.formatNational(), country: p.country as PhoneCountryCode }
      }
    } catch {
      /* numéro non parsable : on retombe sur le défaut */
    }
  }
  return { national: '', country: DEFAULT_COUNTRY }
}

function startEdit() {
  const m = member.value
  if (!m) return
  const phone = parseMemberPhone(m.phone)
  form.phone = phone.national
  form.phoneCountry = phone.country
  addr.line1 = m.address.line1
  addr.line2 = m.address.line2
  addr.city = m.address.city
  addr.postcode = m.address.postalCode
  addr.country = (m.address.country || DEFAULT_COUNTRY) as AddressCountryCode
  form.ssn = ''
  ssnError.value = null
  saveError.value = null
  done.value = false
  editing.value = true
}

/** Grouped as the member types, the way the subscription tunnel presents it. */
function onSsnInput(event: Event) {
  const input = event.target as HTMLInputElement
  form.ssn = formatSsn(input.value)
  ssnError.value = null
}
function cancelEdit() {
  editing.value = false
}
async function submitEdit() {
  saving.value = true
  saveError.value = null

  // Téléphone : conversion en E.164 (refus si la saisie est invalide).
  const phoneRaw = form.phone.trim()
  const phoneE164 = phoneRaw ? toE164(phoneRaw, form.phoneCountry) : ''
  if (phoneRaw && phoneE164 === null) {
    saveError.value = t('auth.account.fields.phone.invalid')
    saving.value = false
    return
  }

  // Adresse française : validation finale de la commune (ville / code postal).
  if (addressFields.value && !(await addressFields.value.validate())) {
    saving.value = false
    return
  }

  // The NIR is optional here: a member who opens the form to change their phone number is not
  // asked for it. Left empty it is simply not sent, and only a filled value is validated, with
  // the same rules and the same messages as the subscription tunnel.
  const ssn = canEnterSsn.value ? normalizeSsn(form.ssn) : ''
  if (ssn) {
    const code = validateSsn(ssn)
    if (code) {
      ssnError.value = t(SSN_ERROR_KEYS[code])
      saving.value = false
      return
    }
  }

  try {
    const line1 = addr.line1.trim()
    const line2 = addr.line2.trim()
    const city = addr.city.trim()
    const postcode = addr.postcode.trim()
    // Mise à jour partielle : on n'envoie le bloc adresse que s'il est renseigné
    // (le complément seul ne compte pas). Sinon, seul le téléphone est mis à jour
    // (l'adresse est laissée intacte côté back).
    const address =
      line1 || city || postcode
        ? { line1, line2, postalCode: postcode, city, country: addr.country || DEFAULT_COUNTRY }
        : undefined
    await updateProfile({ phoneNumber: phoneE164 ?? '', socialSecurityNumber: ssn || undefined }, address)
    await reloadMember()
    editing.value = false
    done.value = true
  } catch (err) {
    saveError.value =
      err instanceof ApiError && err.status === 422
        ? t('auth.account.info.invalidError')
        : t('auth.account.info.saveError')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <AccountLayout v-if="member" active="info">
    <section class="inf">
      <!-- Fil d'ariane + titre -->
      <nav class="inf__crumb" aria-label="breadcrumb">
        <span>{{ t('auth.account.info.breadcrumbHome') }}</span>
        <span class="inf__crumb-sep" aria-hidden="true">›</span>
        <span class="inf__crumb-current">{{ t('auth.account.info.navTitle') }}</span>
      </nav>
      <header class="inf__head">
        <h1 class="inf__title">{{ t('auth.account.info.navTitle') }}</h1>
        <p class="inf__subtitle">{{ t('auth.account.info.subtitle') }}</p>
      </header>

      <div class="inf__grid">
        <!-- Colonne principale -->
        <div class="inf__main">
          <!-- En-tête de carte : intro + contact -->
          <div class="lead">
            <span class="lead__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <circle cx="12" cy="8" r="3.5" fill="none" stroke="currentColor" stroke-width="1.6"/>
                <path d="M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </span>
            <div class="lead__body">
              <h2 class="lead__title">{{ t('auth.account.info.title') }}</h2>
              <p class="lead__desc">{{ t('auth.account.info.cardDesc') }}</p>
            </div>
            <RouterLink class="btn-dark lead__cta" :to="contactHref">{{ t('auth.account.info.contactCta') }}</RouterLink>
          </div>

          <!-- Liste des informations (lecture seule) -->
          <dl v-if="!editing" class="rows">
            <div v-for="r in rows" :key="r.key" class="row">
              <span class="row__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <template v-if="r.icon === 'user'">
                    <circle cx="12" cy="8" r="3.5" fill="none" stroke="currentColor" stroke-width="1.6"/>
                    <path d="M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                  </template>
                  <template v-else-if="r.icon === 'cake'">
                    <path d="M4 20h16v-7H4zM6 13v-3h12v3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                    <path d="M12 7V4M9 7V5.5M15 7V5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                  </template>
                  <template v-else-if="r.icon === 'shield'">
                    <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                  </template>
                  <template v-else-if="r.icon === 'hash'">
                    <path d="M9 4 7 20M17 4l-2 16M5 9h14M4 15h14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                  </template>
                  <template v-else-if="r.icon === 'phone'">
                    <path d="M6 3h3l1.5 5-2 1.5a11 11 0 0 0 5 5l1.5-2 5 1.5v3a2 2 0 0 1-2 2A16 16 0 0 1 4 5a2 2 0 0 1 2-2Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                  </template>
                  <template v-else>
                    <path d="M12 21s-6-5-6-10a6 6 0 0 1 12 0c0 5-6 10-6 10Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                    <circle cx="12" cy="11" r="2.2" fill="none" stroke="currentColor" stroke-width="1.6"/>
                  </template>
                </svg>
              </span>
              <dt class="row__label">{{ t('auth.account.info.' + r.key) }}</dt>
              <dd class="row__value">
                <span class="row__text">
                  {{ r.value }}
                  <!-- Le crayon n'est pas un ornement : il ouvre le formulaire, comme le bouton
                       en bas de carte. Un pictogramme qui ne fait rien se clique quand même. -->
                  <button
                    v-if="r.editable"
                    type="button"
                    class="row__edit"
                    :aria-label="t('auth.account.info.editField', { field: t('auth.account.info.' + r.key) })"
                    @click="startEdit"
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                      <path d="M4 20h4L19 9l-4-4L4 16z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
                      <path d="M14 6l4 4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
                    </svg>
                  </button>
                </span>
              </dd>
            </div>

            <!-- Adresse (sur deux lignes) -->
            <div class="row">
              <span class="row__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path d="M12 21s-6-5-6-10a6 6 0 0 1 12 0c0 5-6 10-6 10Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                  <circle cx="12" cy="11" r="2.2" fill="none" stroke="currentColor" stroke-width="1.6"/>
                </svg>
              </span>
              <dt class="row__label">{{ t('auth.account.info.address') }}</dt>
              <dd class="row__value">
                <span class="row__text">
                  {{ member.address.line1 }}
                  <button
                    type="button"
                    class="row__edit"
                    :aria-label="t('auth.account.info.editField', { field: t('auth.account.info.address') })"
                    @click="startEdit"
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                      <path d="M4 20h4L19 9l-4-4L4 16z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
                      <path d="M14 6l4 4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
                    </svg>
                  </button>
                </span>
                <span v-if="member.address.line2">{{ member.address.line2 }}</span>
                <span>{{ member.address.postalCode }} {{ member.address.city }}</span>
                <span v-if="member.address.country">{{ countryName(member.address.country) }}</span>
              </dd>
            </div>
          </dl>

          <!-- Formulaire d'édition (NIR si absent, téléphone, adresse) -->
          <form v-if="editing" class="edit-form" novalidate @submit.prevent="submitEdit">
            <!-- Le NIR n'apparaît que s'il manque : une fois enregistré il n'est plus
                 modifiable, et le champ est absent plutôt que désactivé. -->
            <div v-if="canEnterSsn" class="field">
              <label class="field__label" for="edit-nir">{{ t('auth.account.info.nir') }}</label>
              <input
                id="edit-nir"
                class="jf-input"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                maxlength="21"
                :value="form.ssn"
                :aria-invalid="ssnError ? 'true' : undefined"
                aria-describedby="edit-nir-hint"
                :placeholder="t('auth.account.info.nirPlaceholder')"
                @input="onSsnInput"
              />
              <p id="edit-nir-hint" class="field__hint">{{ t('auth.account.info.nirHint') }}</p>
              <p v-if="ssnError" class="inf__error" role="alert">{{ ssnError }}</p>
            </div>
            <div class="field">
              <label class="field__label" for="edit-phone">{{ t('auth.account.info.phone') }}</label>
              <PhoneField v-model="form.phone" v-model:country="form.phoneCountry" input-id="edit-phone" />
            </div>
            <AddressFields
              ref="addressFields"
              v-model:line1="addr.line1"
              v-model:line2="addr.line2"
              v-model:country="addr.country"
              v-model:city="addr.city"
              v-model:postcode="addr.postcode"
            />
            <p v-if="saveError" class="inf__error" role="alert">{{ saveError }}</p>
            <div class="edit-actions">
              <button type="submit" class="btn-dark" :disabled="saving">
                {{ saving ? t('auth.account.info.saving') : t('auth.account.info.save') }}
              </button>
              <button type="button" class="btn-ghost" @click="cancelEdit">{{ t('auth.account.info.cancel') }}</button>
            </div>
          </form>

          <p v-if="!editing && done" class="inf__success" role="status">{{ t('auth.account.info.saved') }}</p>

          <!-- Un changement à déclarer ? -->
          <article v-if="!editing" class="change">
            <span class="change__visual" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="28" height="28">
                <path d="M4 7h11M4 7l3-3M4 7l3 3" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M20 17H9M20 17l-3-3M20 17l-3 3" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <div class="change__body">
              <h3 class="change__title">{{ t('auth.account.info.changeTitle') }}</h3>
              <p class="change__desc">{{ t('auth.account.info.changeDesc') }}</p>
            </div>
            <button type="button" class="btn-dark change__cta" :disabled="editing" @click="startEdit">{{ t('auth.account.info.editAll') }}</button>
          </article>
        </div>

        <!-- Colonne latérale -->
        <aside class="inf__aside">
          <article class="aside-card">
            <span class="aside-card__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                <path d="M9 12l2 2 4-4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <h2 class="aside-card__title">{{ t('auth.account.info.secureTitle') }}</h2>
            <p class="aside-card__desc">{{ t('auth.account.info.secureDesc') }}</p>
          </article>

          <article class="aside-card">
            <span class="aside-card__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
                <path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.7.4-1.1.9-1.1 1.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                <circle cx="12" cy="16.5" r="1" fill="currentColor"/>
              </svg>
            </span>
            <h2 class="aside-card__title">{{ t('auth.account.info.helpTitle') }}</h2>
            <p class="aside-card__desc">{{ t('auth.account.info.helpDesc') }}</p>
            <RouterLink class="btn-dark aside-card__cta" :to="contactHref">{{ t('auth.account.info.helpCta') }}</RouterLink>
          </article>
        </aside>
      </div>
    </section>
  </AccountLayout>
</template>

<style scoped>
.inf { display: flex; flex-direction: column; gap: 1rem; }

/* Fil d'ariane + titre */
.inf__crumb { display: flex; align-items: center; gap: 0.4rem; font-size: var(--text-sm); color: var(--text-muted); }
.inf__crumb-current { color: var(--color-chocolate); font-weight: 600; }
.inf__title { font-size: var(--text-xl); font-weight: 700; color: var(--color-chocolate); }
.inf__subtitle { margin-top: 0.15rem; font-size: var(--text-sm); color: var(--text-muted); }

/* Grille principale / latérale */
.inf__grid {
  display: grid;
  grid-template-columns: 1.7fr 1fr;
  gap: 1.25rem;
  align-items: start;
  margin-top: 0.25rem;
}
.inf__main {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}
.inf__aside { display: flex; flex-direction: column; gap: 1.25rem; }

/* En-tête de carte */
.lead { display: grid; grid-template-columns: auto 1fr auto; align-items: center; column-gap: 0.9rem; row-gap: 0.6rem; }
.lead__icon {
  display: grid;
  place-items: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: var(--radius-lg);
  color: var(--primary);
  background: color-mix(in oklab, var(--primary) 12%, white);
}
.lead__body { min-width: 0; }
.lead__title { font-size: var(--text-lg); font-weight: 700; color: var(--color-chocolate); }
.lead__desc { margin-top: 0.15rem; font-size: var(--text-sm); color: var(--text-muted); line-height: var(--leading-normal); }
.lead__cta { text-decoration: none; }
@media (max-width: 40rem) {
  .lead { grid-template-columns: auto 1fr; }
  .lead__cta { grid-column: 1 / -1; justify-self: start; }
}

/* Liste des informations */
.rows { display: flex; flex-direction: column; margin: 0; }
.row {
  display: grid;
  grid-template-columns: auto 11rem 1fr;
  align-items: center;
  gap: 0.5rem 0.9rem;
  padding: 0.8rem 0.25rem;
  border-top: 1px solid var(--border);
}
.row:first-child { border-top: 0; }
.row__icon {
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: var(--radius-md);
  color: var(--primary);
  background: color-mix(in oklab, var(--primary) 10%, white);
}
.row__label { font-size: var(--text-sm); font-weight: 600; color: var(--color-chocolate); }
.row__value { display: flex; flex-direction: column; gap: 0.3rem; font-size: var(--text-sm); color: var(--text-muted); min-width: 0; }
.row__text { display: inline-flex; align-items: center; gap: 0.4rem; }
.row__edit {
  display: inline-grid;
  place-items: center;
  padding: 0.15rem;
  border: 0;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: color var(--dur-fast) var(--ease-out);
}
.row__edit:hover { color: var(--primary); }
.row__edit:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--focus-ring); }
.row__input {
  width: 100%;
  padding: 0.5rem 0.7rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  background: var(--color-white);
  font: inherit;
  font-size: var(--text-sm);
  color: var(--color-chocolate);
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.row__input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--focus-ring);
}
.row__input-group { display: flex; gap: 0.5rem; }
.row__input--zip { max-width: 7rem; }
@media (max-width: 40rem) {
  .row { grid-template-columns: auto 1fr; }
  .row__value { grid-column: 2; }
}

/* Barre d'actions d'édition */
.inf__error { font-size: var(--text-sm); color: var(--color-cherry); }
.inf__success { font-size: var(--text-sm); color: var(--success); }
.edit-form { display: flex; flex-direction: column; gap: 1rem; }
.edit-form .field { display: flex; flex-direction: column; gap: 0.4rem; }
.edit-form .field__label { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.edit-form .jf-input {
  width: 100%;
  padding: 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--text-base);
  color: var(--color-chocolate);
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.edit-form .field__hint { font-size: var(--text-sm); color: var(--text-muted); }
.edit-form .jf-input::placeholder { color: var(--text-muted); }
.edit-form .jf-input:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--focus-ring); }
.edit-actions { display: flex; flex-wrap: wrap; gap: 0.6rem; }
.btn-ghost {
  padding: 0.7rem 1.4rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);
  background: var(--color-white);
  color: var(--color-chocolate);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out);
}
.btn-ghost:hover { border-color: var(--primary); }

/* Un changement à déclarer ? */
.change {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  column-gap: 0.9rem;
  row-gap: 0.6rem;
  padding: 1rem;
  border-radius: var(--radius-lg);
  background: color-mix(in oklab, var(--color-peach) 35%, white);
  border: 1px solid color-mix(in oklab, var(--primary) 22%, white);
}
.change__visual {
  display: grid;
  place-items: center;
  width: 3rem;
  height: 3rem;
  flex-shrink: 0;
  border-radius: var(--radius-lg);
  color: var(--primary);
  background: var(--color-white);
}
.change__title { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.change__desc { margin-top: 0.15rem; font-size: var(--text-xs); color: var(--color-chocolate); line-height: var(--leading-normal); }
.change__cta { text-decoration: none; }
@media (max-width: 40rem) {
  .change { grid-template-columns: auto 1fr; }
  .change__cta { grid-column: 1 / -1; justify-self: start; }
}

/* Cartes latérales */
.aside-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}
.aside-card__icon {
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--radius-lg);
  color: var(--primary);
  background: color-mix(in oklab, var(--primary) 12%, white);
}
.aside-card__title { font-size: var(--text-base); font-weight: 700; color: var(--color-chocolate); }
.aside-card__desc { font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }
.aside-card__cta { margin-top: 0.2rem; text-decoration: none; }

/* Bouton sombre */
.btn-dark {
  padding: 0.7rem 1.4rem;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--color-chocolate);
  color: var(--color-white);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease-out);
}
.btn-dark:hover { background: color-mix(in oklab, var(--color-chocolate) 85%, white); }
.btn-dark:disabled { opacity: 0.5; cursor: default; }

@media (max-width: 64rem) {
  .inf__grid { grid-template-columns: 1fr; }
}
</style>
